
/**
 * bootstrap.tsx
 * 
 * A special bootstrapping module to prevent TDZ errors in production.
 * This module carefully initializes dependencies in the correct order.
 */

// Use the module resolver to ensure ESM compatibility
import { createElement, StrictMode, Fragment } from './utils/module-resolver';
// Re-export as React for compatibility with existing code
const React = { createElement, StrictMode, Fragment };

// This module handles the actual app initialization
// It uses dynamic imports to prevent TDZ errors
const initializeApp = async () => {
  try {
    // First, dynamically import i18n (which has many dependencies)
    await import('./i18n');
    
    // Then import React DOM after i18n is initialized
    const ReactDOM = await import('react-dom/client');
    
    // Import all UI-related dependencies first
    const [
      reactRouterModule,
      sonnerModule,
      reactQueryModule,
      // Import other core UI dependencies here
    ] = await Promise.all([
      import('react-router-dom'),
      import('sonner'),
      import('@tanstack/react-query'),
      // Add other imports as needed
    ]);
    
    // Only after UI dependencies are loaded, import app-specific modules
    const [AppModule, AuthContextModule] = await Promise.all([
      import('./App'),
      import('./context/AuthContext'),
    ]);

    const { BrowserRouter } = reactRouterModule;
    const { Toaster } = sonnerModule;
    const { QueryClient, QueryClientProvider } = reactQueryModule;
    const App = AppModule.default;
    const { AuthProvider } = AuthContextModule;
    
    // Create QueryClient instance
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          retry: 1,
          refetchOnWindowFocus: false,
        },
      },
    });
    
    // Get the root element after all modules are loaded
    const rootElement = document.getElementById('root');
    if (!rootElement) {
      throw new Error('Root element not found');
    }
    
    // Create root and render app
    const root = ReactDOM.createRoot(rootElement);
    
    // Use standard JSX syntax
    root.render(
      React.createElement(React.StrictMode, null,
        React.createElement(QueryClientProvider, { client: queryClient },
          React.createElement(BrowserRouter, {
            future: {
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }
          },
            React.createElement(AuthProvider, null,
              React.createElement(React.Fragment, null,
                React.createElement(App, null),
                React.createElement(Toaster, { position: "top-right", richColors: true, closeButton: true })
              )
            )
          )
        )
      )
    );
    
    return true;
  } catch (error) {
    console.error('Bootstrap error:', error);
    
    // Show friendly error message
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="padding: 20px; font-family: sans-serif;">
          <h2 style="color: #e53e3e;">Application Error</h2>
          <p>We're sorry, but there was an error loading the application.</p>
          <p>Error details: ${error instanceof Error ? error.message : 'Unknown error'}</p>
          <button onclick="window.location.reload()">Reload Application</button>
        </div>
      `;
    }
    
    return false;
  }
};

export default initializeApp;
